/*
 * CYCLIQ FIT : moteur de qualification commerciale, totalement séparé du
 * Diagnostic CYCLIQ.
 *
 * Règles absolues :
 * - N'utilise AUCUNE réponse des questions Q4 à Q24 du diagnostic.
 * - N'utilise aucun score santé, aucun profil CYCLIQ, aucun safety_flag.
 * - Le score FIT est strictement commercial et n'est JAMAIS affiché.
 * - Calcul local et déterministe : mêmes réponses, même résultat.
 * - AUCUN tarif n'est affiché dans le parcours : l'investissement est
 *   annoncé uniquement pendant l'appel avec Gino (V2.1).
 */

export const FIT_QUESTIONS = [
  {
    id: "fit_delai",
    field: "desired_start_time",
    weight: 0.35,
    type: "choice",
    text: "Quand souhaiterais tu commencer un accompagnement structuré si celui ci correspond réellement à tes besoins ?",
    options: [
      { value: "immediatement", label: "Immédiatement", score: 100 },
      { value: "30_jours", label: "Dans les 30 jours", score: 90 },
      { value: "3_mois", label: "Dans les 3 mois", score: 60 },
      { value: "plus_tard", label: "Plus tard", score: 30 },
      { value: "informations", label: "Je cherche uniquement des informations", score: 0 },
    ],
  },
  {
    id: "fit_temps",
    field: "weekly_time_commitment",
    weight: 0.20,
    type: "choice",
    text: "Combien de temps peux tu raisonnablement consacrer chaque semaine à ta progression ?",
    options: [
      { value: "moins_2h", label: "Moins de 2 heures", score: 30 },
      { value: "2_3h", label: "2 à 3 heures", score: 70 },
      { value: "3_5h", label: "3 à 5 heures", score: 100 },
      { value: "plus_5h", label: "Plus de 5 heures", score: 100 },
    ],
  },
  {
    id: "fit_importance",
    field: "change_priority",
    weight: 0.25,
    type: "scale",
    text: "À quel point est il important pour toi d'améliorer cette situation maintenant ?",
    min: 0,
    max: 10,
    min_label: "Pas prioritaire",
    max_label: "Très important",
  },
  {
    id: "fit_decision",
    field: "decision_autonomy",
    weight: 0.20,
    type: "choice",
    text: "Si CYCLIQ EXCELLENCE correspond à tes besoins, peux tu prendre seule la décision de rejoindre l'accompagnement ?",
    options: [
      { value: "oui", label: "Oui", score: 100 },
      { value: "en_parler", label: "Je souhaite en parler avec quelqu'un mais la décision reste la mienne", score: 70 },
      { value: "autre_personne", label: "La décision dépend également d'une autre personne", score: 40 },
      { value: "ne_sais_pas", label: "Je ne sais pas encore", score: 20 },
    ],
  },
];

export const FIT_DEFAULT_THRESHOLDS = { high: 75, qualified: 55 };

/* Arrondi identique à Python : arrondi bancaire au pair le plus proche. */
function pyRound(x) {
  const floor = Math.floor(x);
  const diff = x - floor;
  if (diff < 0.5) return floor;
  if (diff > 0.5) return floor + 1;
  return floor % 2 === 0 ? floor : floor + 1;
}

function questionFitScore(question, raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  if (question.type === "scale") {
    const value = Number(raw);
    if (Number.isNaN(value)) return null;
    const clamped = Math.max(question.min, Math.min(question.max, value));
    return clamped * 10;
  }
  const option = question.options.find((o) => o.value === raw);
  return option ? option.score : null;
}

/*
 * Score FIT sur 100 : moyenne pondérée des 4 questions (toutes requises).
 * Retourne null si une question manque.
 */
export function computeFitScore(answers) {
  let total = 0;
  for (const question of FIT_QUESTIONS) {
    const score = questionFitScore(question, answers[question.id]);
    if (score === null) return null;
    total += score * question.weight;
  }
  return pyRound(total);
}

/*
 * Orientation commerciale :
 * - nurture : recherche d'informations seulement, ou score sous le seuil
 *   qualifié ;
 * - high_priority : score au dessus du seuil prioritaire ;
 * - qualified : entre les deux.
 * Le safety_flag n'entre JAMAIS dans ce calcul. Aucune donnée budgétaire
 * n'est collectée : l'investissement est annoncé pendant l'appel.
 */
export function determineFitStatus(score, answers, thresholds = FIT_DEFAULT_THRESHOLDS) {
  if (score === null) return null;
  if (answers.fit_delai === "informations") return "nurture";
  if (score >= (thresholds.high ?? FIT_DEFAULT_THRESHOLDS.high)) return "high_priority";
  if (score >= (thresholds.qualified ?? FIT_DEFAULT_THRESHOLDS.qualified)) return "qualified";
  return "nurture";
}

/* Charge utile commerciale envoyée au backend (liste blanche stricte). */
export function buildFitSyncPayload(journeyId, answers, score, status, utm) {
  return {
    journey_id: journeyId,
    cycliq_fit_score: score,
    cycliq_fit_status: status,
    desired_start_time: answers.fit_delai,
    weekly_time_commitment: answers.fit_temps,
    change_priority: Number(answers.fit_importance),
    decision_autonomy: answers.fit_decision,
    utm: utm || {},
  };
}
